import {
    AfterViewInit,
    ChangeDetectorRef,
    Directive,
    ElementRef,
    HostBinding,
    inject,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Renderer2,
    SimpleChanges,
    ViewRef
} from '@angular/core';
import { NavigationEnd } from '@angular/router';
import { App } from '@df/core/app';
import { Meta } from '@df/core/meta/meta';
import { Client } from '@df/core/service/client';
import { resizeObservable } from '@df/dom/fn/resize-observable';
import { TimeoutService } from '@df/ng/timeout.service';
import { fromEvent, merge, Observable, Subject, Subscription } from 'rxjs';
import { filter, mergeMap, takeUntil, tap } from 'rxjs/operators';

@Directive({
    selector: '[dragScroll]',
    standalone: true
})
export class DragScrollDirective implements OnChanges, OnInit, AfterViewInit, OnDestroy {
    @Input()
    scrollToIndex?: number;

    @Input()
    scrollIntoView?: boolean;

    @Input()
    dragscrollFade = true;

    @HostBinding('class.dragscroll')
    dragscroll = true;

    @HostBinding('class.dragscroll-start')
    dragscrollStart = true;

    @HostBinding('class.dragscroll-end')
    dragscrollEnd = false;

    @HostBinding('class.dragscroll-dragging')
    dragscrollDragging = false;

    @HostBinding('class.dragscroll-shadow')
    dragscrollShadow = false;

    @HostBinding('class.dragscroll-can-drag')
    dragscrollCanDrag = false;

    private readonly destroyed$ = new Subject<undefined>();
    private _mutationObserver!: MutationObserver;
    protected subscriptions: Subscription = new Subscription();
    protected move$!: Observable<MouseEvent | TouchEvent>;
    protected down$!: Observable<MouseEvent | TouchEvent>;
    protected leave$!: Observable<MouseEvent | TouchEvent>;
    protected isDown = false;
    protected isDragging = false;
    protected state?: boolean;
    protected startX!: number;
    protected scrollLeft!: number;
    protected wrapperOffsetWidth!: number;
    protected wrapperScrollWidth!: number;
    wrapper!: HTMLElement;

    private meta = inject(Meta);

    constructor(
        protected changeDetectorRef: ChangeDetectorRef,
        protected elementRef: ElementRef,
        protected ngZone: NgZone,
        protected renderer: Renderer2,
        protected app: App,
        protected client: Client,
        protected timeoutService: TimeoutService
    ) {
        this.meta.routerEvents
            .pipe(
                filter(e => e instanceof NavigationEnd),
                takeUntil(this.destroyed$)
            )
            .subscribe(() => this.timeoutService.setTimeout(() => this._scrollToIndex(this.scrollToIndex), 100));
    }

    ngOnInit(): void {
        if (this.app.isPlatformBrowser) {
            this.wrapper = this.renderer.createElement('div');
            this.renderer.setStyle(this.wrapper, 'overflow', 'hidden');
            this.renderer.setStyle(this.wrapper, 'white-space', 'nowrap');
            this.renderer.setStyle(this.wrapper, 'vertical-align', 'top');

            this.wrapAll(this.elementRef.nativeElement, this.wrapper);

            this.init();

            this.subscriptions.add(this.client.obs.resize.subscribe(() => this.check()));
        }
    }

    ngAfterViewInit(): void {
        if (this.app.isPlatformBrowser) {
            this._scrollToIndex(this.scrollToIndex);

            this.check();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.app.isPlatformBrowser && changes['scrollToIndex'] && this.wrapper) {
            this._scrollToIndex(this.scrollToIndex);
        }
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    protected init(): void {
        this.ngZone.runOutsideAngular(() => {
            this.down$ = merge(fromEvent<MouseEvent>(this.wrapper, 'mousedown'), fromEvent<TouchEvent>(this.wrapper, 'touchstart'));
            this.leave$ = merge(
                fromEvent<MouseEvent>(this.wrapper, 'mouseleave'),
                fromEvent<MouseEvent>(document, 'mouseup'),
                fromEvent<TouchEvent>(document, 'touchend')
            );
            this.move$ = merge(fromEvent<MouseEvent>(document, 'mousemove'), fromEvent<TouchEvent>(document, 'touchmove'));

            this.subscriptions.add(
                this.down$
                    .pipe(
                        tap((event: MouseEvent | TouchEvent) => {
                            this.isDown = true;
                            const x: number = this.getEventPageX(event);

                            this.startX = x - this.wrapper.offsetLeft;
                            this.scrollLeft = this.wrapper.scrollLeft;
                        }),
                        mergeMap(_ =>
                            this.move$.pipe(
                                // complete inner observable on mouseleave event
                                takeUntil(this.leave$)
                            )
                        )
                    )
                    .subscribe(event => this.move(event))
            );

            this.subscriptions.add(
                this.leave$
                    .pipe(
                        tap(() => {
                            this.isDown = false;
                            this.isDragging = false;
                            this.dragscrollDragging = false;
                            this.detectChanges();
                        })
                    )
                    .subscribe()
            );

            if (typeof MutationObserver !== 'undefined') {
                this._mutationObserver = new MutationObserver(() => this.check());
                this._mutationObserver.observe(this.wrapper, {
                    childList: true,
                    attributes: true,
                    subtree: true
                });
            }

            this.subscriptions.add(
                resizeObservable(this.wrapper, () => {
                    this.check();
                }).subscribe()
            );
        });
    }

    protected move(event: MouseEvent | TouchEvent): void {
        if (!this.isDown || !this.dragscrollCanDrag) {
            return;
        }

        if (!this.isDragging) {
            this.isDragging = true;
            this.dragscrollDragging = true;
            this.detectChanges();
        }

        const pgX: number = this.getEventPageX(event);
        const x = pgX - this.wrapper.offsetLeft;
        const walk = x - this.startX;

        this.wrapper.scrollLeft = this.scrollLeft - walk;

        this.applyClass();
    }

    protected check(): void {
        if (this.wrapperOffsetWidth !== this.wrapper.offsetWidth || this.wrapperScrollWidth !== this.wrapper.scrollWidth) {
            this.wrapperOffsetWidth = this.wrapper.offsetWidth;
            this.wrapperScrollWidth = this.wrapper.scrollWidth;

            this.dragscrollCanDrag = this.wrapperOffsetWidth < this.wrapperScrollWidth;
            this.dragscrollShadow = this.dragscrollCanDrag && this.dragscrollFade;

            this.detectChanges();
        }
    }

    protected getEventPageX(event: MouseEvent | TouchEvent): number {
        if (event instanceof MouseEvent) {
            return event.pageX;
        }

        if (event instanceof TouchEvent) {
            return event.touches[0].pageX;
        }

        return 0;
    }

    applyClass(): void {
        if (this.wrapper.scrollLeft < 10) {
            this.toggleClass(true);
        } else if (this.wrapper.scrollWidth - this.wrapper.scrollLeft < this.wrapper.offsetWidth + 10) {
            this.toggleClass(false);
        } else {
            this.toggleClass();
        }
    }

    protected toggleClass(start?: boolean): void {
        if (this.state !== start) {
            this.state = start;

            if (start !== undefined) {
                if (start) {
                    this.dragscrollStart = true;
                    this.dragscrollEnd = false;
                } else {
                    this.dragscrollEnd = true;
                    this.dragscrollStart = false;
                }
            } else {
                this.dragscrollEnd = false;
                this.dragscrollStart = false;
            }

            this.detectChanges();
        }
    }

    protected _scrollToIndex(index?: number): void {
        if (index !== undefined) {
            const childrenElement = this.wrapper.children[index] as HTMLElement;

            if (childrenElement) {
                if (this.scrollIntoView) {
                    childrenElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
                } else {
                    this.wrapper.scrollLeft = childrenElement.offsetLeft;
                }
            }
        }
    }

    protected wrapAll(target: any, wrapper: HTMLElement): HTMLElement {
        [...target.childNodes].forEach(child => wrapper.appendChild(child));
        target.appendChild(wrapper);

        return wrapper;
    }

    protected detectChanges(): void {
        if (!(<ViewRef>this.changeDetectorRef).destroyed) {
            this.changeDetectorRef.detectChanges();
        }
    }
}
